/**
 * The universal composer's task-screen door (spec 2026-09-20 §3.2): the wire
 * shape of `POST /api/composer/route`. The server reads a plain sentence typed
 * on a task's own screen with the task in hand and says which of three EXISTING
 * rails it rides: the task (today's socket send), one of this task's screen
 * controls, or the composer. `why` is the rung that decided, for the log line
 * and the eval; a client acts on `to` alone and never on `why`.
 */

/** The fixed list of this task's screen controls (spec §3.3): open or close the computer view, stop the task. Nothing that spends, sends, deletes or judges. */
export const TASK_DOOR_CONTROLS = ['open_computer', 'close_computer', 'stop'] as const;
export type TaskDoorControl = (typeof TASK_DOOR_CONTROLS)[number];

export function isTaskDoorControl(x: unknown): x is TaskDoorControl {
  return typeof x === 'string' && (TASK_DOOR_CONTROLS as readonly string[]).includes(x);
}

/**
 * The rung that decided (plan R-P1). `jev`: the reflex cleared a bar. `bar_miss`: it
 * answered under every bar, so the words are the task's. `flag_off`: the server's door
 * is dark. `fault`: Jev faulted or the deadline passed. `no_task`: the id is not a task
 * of theirs. `not_asked`: a blank or over-long sentence, never sent to the reflex.
 */
export type TaskDoorWhy = 'jev' | 'bar_miss' | 'flag_off' | 'fault' | 'no_task' | 'not_asked';

export type TaskDoorRoute =
  | { to: 'task'; why: TaskDoorWhy }
  /** `words` is the one short line the inline card shows for a beat; server-minted, never client-written. */
  | { to: 'control'; control: TaskDoorControl; words: string; why: 'jev' }
  | { to: 'composer'; why: 'jev' };

export interface TaskDoorRouteRequest {
  taskId: string;
  text: string;
}

/** The one answer every non-jev rung gives: the words go to the task, as today. */
export const toTask = (why: Exclude<TaskDoorWhy, 'jev'>): TaskDoorRoute => ({ to: 'task', why });

/** The cap on a sentence the door asks about (R-L23): a longer one is an instruction, the task's. The server refuses it at the wire and the client never asks; ONE number, so the two sides agree. */
export const TASK_DOOR_MAX_CHARS = 300;
